/**
 * ContentCmsShortcut — floating admin-only pill that appears on every
 * sister-portal page (worldpulse, usapulse, indias, kerala) linking
 * to `/admin/content-engine`. The route is already mounted on every
 * hostname — this component just makes it discoverable.
 *
 * Only renders when:
 *   • The current user is signed in AND
 *   • has role=admin (or the AuthContext flags them as admin) AND
 *   • we're NOT already on an /admin/... path (avoid self-linking)
 *
 * Positioned bottom-right so it never interferes with the portal's
 * own chrome.
 */
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function ContentCmsShortcut() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try { setDismissed(sessionStorage.getItem("cms-shortcut-dismissed") === "1"); } catch {}
  }, []);

  const isAdmin = !!user && (
    user.is_admin === true ||
    user.role === "admin" ||
    (Array.isArray(user.roles) && user.roles.includes("admin"))
  );
  if (!isAdmin) return null;
  if (pathname.startsWith("/admin/content-engine")) return null;
  if (dismissed) return null;

  return (
    <Link
      to="/admin/content-engine"
      data-testid="content-cms-shortcut"
      className="fixed bottom-4 right-4 z-40 inline-flex items-center gap-1.5 rounded-full bg-violet-600 hover:bg-violet-700 text-white font-semibold px-3.5 py-2 text-xs shadow-lg ring-1 ring-violet-500/30"
      onAuxClick={(e) => {
        // Middle-click dismisses for the current tab.
        if (e.button === 1) {
          e.preventDefault();
          try { sessionStorage.setItem("cms-shortcut-dismissed", "1"); } catch {}
          setDismissed(true);
        }
      }}
      title="AI Content Engine CMS (middle-click to hide for this session)"
    >
      <Sparkles className="w-3.5 h-3.5" /> Content CMS
    </Link>
  );
}
